import React, { useEffect, useMemo, useState } from 'react';
import copy from 'copy-to-clipboard';
import { useAccount, usePublicClient, useWaitForTransactionReceipt, useWalletClient } from 'wagmi';
import Web3 from 'web3';

import { GenerateReferralURL } from 'utils/Referrals';
import { ToastErrorTx, ToastProgressTx, ToastSuccessTx } from 'utils/Toasts/transaction';
import { PopupReferrals } from 'utils/Popups';
import {
  useGameDetails, useIsWantedChainActive, useKeyPress, useReferrerFromBlockchain
} from 'utils/Hooks';
import { CONTRACTS, ABI, USE_MAINNET, ZERO_ADDRESS } from 'const';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { faClone } from '@fortawesome/free-regular-svg-icons';
import './styles.scss';


function ReferralsComponent(props) {
  const gameDetails = useGameDetails('bnb');
  const isWantedChainActive = useIsWantedChainActive();
  //const referrer = useReferrerFromBlockchain();
  const availableGames = useMemo(GetAvailableGames, []);

  const [urlPopupShown, SetWhetherURLPopupShown] = useState(false);
  const [fee, SetFee] = useState(1);
  const [referrer, SetReferrer] = useState('');
  const [rewards, SetRewards] = useState(null);
  const [nOfReferrals, SetReferralQuantity] = useState(0);
  const [selectedGame, SetSelectedGame] = useState(availableGames[0]);
  const [refLinkCopiedSuccessfully, SetRefLinkCopied] = useState(0);

  const [betTxHash, SetTxHash] = useState(null);
  const [formattedError, SetFormattedError] = useState('');

  const walletClient = useWalletClient({ chainId: gameDetails.wantedChain.id });
  const publicClient = usePublicClient();
  const account = useAccount();


  const {
    isSuccess: isTxConfirmed_hook, failureReason: txFailReason
  } = useWaitForTransactionReceipt({ hash: betTxHash, confirmations: 1 });


  useEffect(() => {
    DetermineTxStatusFromHooks()
  }, [isTxConfirmed_hook, betTxHash, txFailReason]);

  useEffect(() => {
    HandleTxError();
  }, [formattedError, betTxHash]);


  function DetermineTxStatusFromHooks() {
    if (isTxConfirmed_hook && betTxHash) {
      console.info(`TX CONFIRMED by hook`);
      ShowTxSuccessToast(betTxHash);
    } else if (txFailReason) {
      /*
      for (const [key, value] of Object.entries(txFailReason)) {
        console.log({ [key]: value });
      }
      */
      console.info(`TX REVERTED according to hook`);
      let reason = 'unknown error';
      const matches = txFailReason.details.match(/:(.*): 0x/);
      if (matches && matches[1])
        reason = matches[1].trim();
      else
        reason = txFailReason.shortMessage.slice(0, 40);
      SetFormattedError(`${reason}`);
    }
  }

  function ShowTxPendingToast() {
    ToastProgressTx();
  }

  function HandleTxError() {
    if (formattedError) {
      ToastErrorTx(formattedError, betTxHash);
      SetTxHash(null); // stop useWaitForTransactionReceipt hook from further updates
      SetFormattedError('');
    }
  }

  function ShowTxSuccessToast(_txHash) {
    if (!_txHash)
      throw new SyntaxError(`missing transaction hash when trying to show success toast`);
    ToastSuccessTx(_txHash);
    SetTxHash(null); // stop useWaitForTransactionReceipt hook from further updates
    SetRewards(0);
  }


  useKeyPress(['Escape'], () => SetWhetherURLPopupShown(false));

  useEffect(() => {
    if (!isWantedChainActive) return;
    UpdateFee();
  }, [gameDetails, isWantedChainActive]); // eslint-disable-line react-hooks/exhaustive-deps 

  useEffect(() => {
    if (!account?.address || !selectedGame || !isWantedChainActive) return;
    UpdateReferralData();
    const interval = setInterval(UpdateReferralData, 5000);
    return () => {
      clearInterval(interval);
    }
  }, [account?.address, gameDetails, selectedGame, isWantedChainActive, publicClient]); // eslint-disable-line react-hooks/exhaustive-deps 


  async function ClaimReferrals() {
    const address = CONTRACTS[gameDetails.wantedChain.name][selectedGame]?.[USE_MAINNET ? 'mainnet' : 'testnet'];
    if (rewards > 0) {
      let simulationResult = null;
      try {
        simulationResult = await publicClient.simulateContract({
          address: address,
          abi: ABI.prediction,
          functionName: 'user_ReferralFundsClaim',
          args: [],
          account: account.address,
          value: 0n,
        });
      } catch (e) {
        if (e?.shortMessage) {
          ToastErrorTx(e.shortMessage);
        }
        return;
      }

      let txHash = '';
      ShowTxPendingToast();
      try {
        txHash = await walletClient.data.writeContract(simulationResult.request);
        SetTxHash(txHash);
      } catch (e) {
        if (e?.shortMessage) {
          SetFormattedError(e.shortMessage);
        } else {
          SetFormattedError(String(e).slice(0, 40));
        }
        SetTxHash(null);
        return;
      }
    }
  }


  function GetAvailableGames() {
    const games = Object.keys(CONTRACTS[gameDetails.wantedChain.name]);
    const referralsGameIndex = games.indexOf('referrals');
    if (referralsGameIndex > -1) {
      games.splice(referralsGameIndex, 1);
    }
    return games;
  }

  function ShowReferralURL() {
    SetWhetherURLPopupShown(true);
  }

  function CopyToClipboard(url) {
    if (!url) throw new SyntaxError('missing required argument: url');
    try {
      copy(url)
      SetRefLinkCopied(1);
    } catch (error) {
      SetRefLinkCopied(2);
    }
  }

  async function UpdateReferralData() {
    if (!account?.address) return;
    UpdateRewards();
    UpdateReferrer();
    UpdateReferralQuantity();
  }

  async function UpdateFee() {
    if (!gameDetails.contracts.referrals) return;
    const feeRaw = await gameDetails.contracts.referrals.methods.referralFee().call();
    // fee in the contract is represented as x% * 10; so 2.5% = 25. We convert it to actual percentage below
    const fee = (Number(feeRaw) / 10).toFixed(2);
    SetFee(fee);
  }

  async function UpdateRewards() {
    const address = CONTRACTS[gameDetails.wantedChain.name][selectedGame]?.[USE_MAINNET ? 'mainnet' : 'testnet'];
    if (address === null) {
      SetRewards(0);
    } else {
      const rewardsRaw = await publicClient.readContract({
        address: address,
        abi: ABI.prediction,
        functionName: 'ReferralRewardsAvailable',
        args: [account?.address],
        //account: account.address,
      });
      const rewards = String(rewardsRaw || 0);

      console.log(`Rewards: ${rewards}`);

      //const rewards = await contract.methods.ReferralRewardsAvailable(account?.address).call();
      const rewardsEther = Web3.utils.fromWei(rewards, 'Ether');
      SetRewards(rewardsEther);
    }
  }



  async function UpdateReferrer() {
    const referrer = await gameDetails.contracts.referrals.methods.GetReferrer(account?.address).call();
    SetReferrer(referrer);
  }
  async function UpdateReferralQuantity() {
    const referrals = await gameDetails.contracts.referrals.methods.GetReferrals(account?.address).call();
    SetReferralQuantity(referrals.length);
  }

  function ReferralURL() {
    const url = GenerateReferralURL(account?.address);
    return (
      <div id='BoxForReferralURL'>
        <textarea value={'https://' + url} disabled />
        <SuccessfulCopyNotification />
        <button id='copy' onClick={() => CopyToClipboard('https://' + url)}>
          <FontAwesomeIcon icon={faClone} size='1x' />
        </button>
        <a id='link' href={('https://' + url)} target='_blank' rel='noopener noreferrer' >
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} size='1x' />
        </a>
      </div>
    );
  }


  function SuccessfulCopyNotification() {
    switch (refLinkCopiedSuccessfully) {
      case 0:
        return null;
      case 1:
        return (<p>Copied</p>);
      case 2:
        return (<p>Error</p>);
      default:
        return null;
    }
  }

  function GameChangerMenu() {
    return (<div className='rewards-menu'>
      <div className='menu-select'>
        <span>{selectedGame.toUpperCase()}</span>
        <FontAwesomeIcon icon={faChevronDown} size='1x' />
      </div>
      <div className='menu-items'>
        {availableGames?.map(item => <div key={item} onClick={() => SetSelectedGame(item)}>
          {item.toUpperCase()}
        </div>)}
      </div>
    </div>);
  }


  function ShowReferralData() {
    let formattedReferrer = '';
    if (referrer == ZERO_ADDRESS || referrer == null)
      formattedReferrer = 'no one';
    else
      formattedReferrer = referrer.slice(0, 6) + '...' + referrer.slice(-4);

    return (
      <React.Fragment>
        <div className='Section'>
          <h2>Referrals</h2>
          <span>(at {fee}% fee)</span>
          <p>You have: <b>{nOfReferrals}</b> referrals</p>
          <button id='ReferralLink' onClick={ShowReferralURL}>
            Get more!
          </button>
        </div>
        <div className='Section'>
          <h2>Rewards</h2>
          <GameChangerMenu />
          <p><b>{rewards} {gameDetails.wantedChain.tickerSymbol}</b><br />available</p>
          <button onClick={ClaimReferrals} disabled={rewards > 0 ? false : true}>
            Claim
          </button>
        </div>
        <div className='Section'>
          <h2>Referrer</h2>
          <p>You are referred by: <b>{formattedReferrer}</b></p>
          <div></div>
        </div>
      </React.Fragment>
    );
  }

  if (!account?.address)
    return (<div className='alert-connect-wallet'>
      <h1>Please connect your wallet</h1>
    </div>);
  else if (!isWantedChainActive)
    return (<div className='alert-connect-wallet'>
      <h1>Wrong Network!</h1>
      <p style={{ color: 'var(--color-theme-white)' }}>
        Change the network in your wallet to {gameDetails.wantedChain.nameFancy}
      </p>
    </div>);
  else
    return (
      <div className='ReferralsPage'>
        <PopupReferrals
          isShown={urlPopupShown}
          closePopup={() => SetWhetherURLPopupShown(false)}
          content={ReferralURL()}
        />
        <ShowReferralData />
      </div>
    );
}

export default ReferralsComponent;
